import React, { useEffect, useRef, useState } from 'react';
import { useSubscription } from '@apollo/client';
import PaymentWidget from 'chimoney-payment-widget';
import { useNavigate } from 'react-router-dom';

import { CHIMONEY_PAYMENT_SUBSCRIPTION } from '../../graphql/mutation';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';

import PaymentModalStyles from './PaymentModal.module.scss';
import { setProfile } from '../../redux/slices/user/profileSlice';
import { setIsGetProfile } from '../../redux/slices/auth/loginSlice';
import { useAppDispatch } from '../../redux/hooks';

interface IPaymentProcessWidget {
  paymentLink: string;
  isOnboardingApartmentSearch?: boolean;
  onSuccess?: any;
  transactionId: string;
  onClickAway: () => void;
  onBack: () => void;
  onClose: () => void;
  isShowCloseButton?: boolean;
  isInAppPreRentGuaranteePaymentApplication?: boolean;
}

const PaymentProcessWidget = ({
  paymentLink,
  isOnboardingApartmentSearch,
  onSuccess,
  transactionId,
  onClickAway,
  onBack,
  onClose,
  isShowCloseButton,
  isInAppPreRentGuaranteePaymentApplication
}: IPaymentProcessWidget) => {
  const [showModal, setShowModal] = useState(true);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data } = useSubscription(CHIMONEY_PAYMENT_SUBSCRIPTION, {
    variables: {
      transactionId
    }
  });
  const requestedStarted = useRef(false);
  const paymentWidget = useRef<any>(null);

  const isSuccessRef = useRef(false);

  const onLogoutUser = () => {
    localStorage.clear();
    dispatch(setProfile({}));
    dispatch(setIsGetProfile(false));
    navigate('/');

    // To clear the current profile and its associated Redux store.
    window.location.reload();
  };

  const completeProcess = () => {
    isSuccessRef.current = true;
    paymentWidget?.current?.close?.();
    if (!requestedStarted.current) return;
    setShowModal(true);
    requestedStarted.current = false;
    onSuccess();
  };

  const initializeWidget = () => {
    setShowModal(false);
    requestedStarted.current = true;
    paymentWidget.current = new PaymentWidget({
      brandColor: '#673AB7',
      brandName: 'Payfi',
      paymentLink,
      onPaymentSuccess: () => {
        completeProcess();
      },
      onWidgetClosed: () => {
        if (isOnboardingApartmentSearch && !isSuccessRef.current) {
          onLogoutUser();
        }
        if (isSuccessRef.current && isInAppPreRentGuaranteePaymentApplication) {
          return;
        }
        onClose();
      }
    });
    paymentWidget?.current?.open();
  };

  useEffect(() => {
    if (paymentLink) {
      initializeWidget();
    }
  }, [paymentLink]);

  useEffect(() => {
    if (
      data?.chimoneyTransaction?.status === 'success' &&
      data?.chimoneyTransaction?.reference === transactionId
    ) {
      completeProcess();
    }
  }, [data]);

  return (
    <Modal
      onClickAway={onClickAway}
      onBack={onBack}
      onClose={onClose}
      isShowCloseIcon={isShowCloseButton}
      isShow={showModal}
      isShowBottomLogo={false}>
      <div className={PaymentModalStyles.wrapper}>
        <div className={PaymentModalStyles.wrapper__title}>Payment in progress</div>

        <div className={PaymentModalStyles.wrapper__loader}>
          <Loader />
        </div>

        <div className={PaymentModalStyles.wrapper__message}>
          Complete your payment on the payment page. if you are not redirected automatically click{' '}
          <button
            className={PaymentModalStyles.wrapper__link}
            onClick={initializeWidget}
            type="button">
            here
          </button>{' '}
          to proceed
        </div>
      </div>
    </Modal>
  );
};

export default PaymentProcessWidget;
